import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { useAnalytics } from 'hooks'
import { LifeBuoy, MessageCircle, TriangleAlert } from 'lucide-react'
import { intercom } from 'services/intercom/intercom.service'

export type SupportHandoffStatus = 'delivered' | 'already_open' | 'failed'

interface SupportHandoffCardProps {
  status: SupportHandoffStatus
}

const COPY: Record<SupportHandoffStatus, { title: string; body: string }> = {
  delivered: {
    title: 'Your message is with the team',
    body: 'A human will reply in the support chat or by email, usually within a day.',
  },
  already_open: {
    title: 'Added to your open support thread',
    body: 'The team has it. A human will reply in the support chat or by email.',
  },
  failed: {
    title: 'Could not reach the team',
    body: 'Your message did not go through. Use the support chat below, or email support@taka.ai.',
  },
}

/**
 * Receipt card for the escalateToSupport tool: the visible proof that the ask
 * reached a human (or, honestly, that it did not). The button opens the
 * Intercom messenger where the created conversation lives, so the user can
 * add more and see the team's reply. The "handoff created" analytics event is
 * emitted server-side by the tool; persisted tool parts replay on every open
 * of the thread, so a render-time event here would count views, not handoffs.
 */
export function SupportHandoffCard({ status }: SupportHandoffCardProps) {
  const { track } = useAnalytics()
  const copy = COPY[status] ?? COPY.failed
  const isFailed = status === 'failed'

  return (
    <div className="my-3 w-full">
      <div
        className={`w-full rounded-[12px] border p-4 ${
          isFailed ? 'border-line-bright bg-bg-2' : 'bg-acid-tint border-line-bright'
        }`}
      >
        <div className="flex items-start gap-3.5">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[10px] border border-line-bright">
            {isFailed ? (
              <TriangleAlert size={20} className="text-paper" />
            ) : (
              <LifeBuoy size={20} className="text-acid" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold text-paper">{copy.title}</p>
            <p className="mt-0.5 text-[12px] text-paper-faint">{copy.body}</p>
            <button
              onClick={() => {
                track(AnalyticsEvents.Support.SUPPORT_HANDOFF_MESSENGER_OPENED, { status })
                intercom.show()
              }}
              className="mt-3 inline-flex items-center gap-1.5 rounded-[6px] border border-line-bright px-3 py-1.5 text-[12px] font-medium text-paper transition-colors hover:bg-bg-3"
            >
              <MessageCircle size={12} />
              Open support chat
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
